/**
 * Resource breakdown charts for hermit-μDC configurator
 * Simple, lightweight charts without external dependencies
 */

import { useMemo } from 'react';
import { Zap, DollarSign } from 'lucide-react';
import { DatacenterConfig, NodeType } from '../../types/configurator';

interface ResourceChartProps {
  config: DatacenterConfig;
  className?: string;
}

const NODE_COLORS: Record<NodeType, { bg: string; bar: string }> = {
  gateway: { bg: 'bg-orange-100', bar: 'bg-orange-500' },
  compute: { bg: 'bg-blue-100', bar: 'bg-blue-500' },
  storage: { bg: 'bg-green-100', bar: 'bg-green-500' },
  daq: { bg: 'bg-purple-100', bar: 'bg-purple-500' },
};

const NODE_LABELS: Record<NodeType, string> = {
  gateway: 'Gateway',
  compute: 'Compute',
  storage: 'Storage',
  daq: 'DAQ',
};

interface NodeStats {
  type: NodeType;
  count: number;
  power: number;
  cost: number;
}

function calculateNodeCost(node: DatacenterConfig['nodes'][0]): number {
  const baseCost =
    node.type === 'gateway' ? 50 :
    node.type === 'storage' ? 200 :
    node.type === 'daq' ? 150 :
    100;
  const specsCost =
    node.specs.cpuCores * 20 +
    node.specs.memoryGB * 5 +
    node.specs.storageGB * 0.5;
  return baseCost + specsCost;
}

export function ResourceChart({ config, className }: ResourceChartProps) {
  const stats = useMemo(() => {
    const byType: Record<NodeType, NodeStats> = {
      gateway: { type: 'gateway', count: 0, power: 0, cost: 0 },
      compute: { type: 'compute', count: 0, power: 0, cost: 0 },
      storage: { type: 'storage', count: 0, power: 0, cost: 0 },
      daq: { type: 'daq', count: 0, power: 0, cost: 0 },
    };

    for (const node of config.nodes) {
      byType[node.type].count++;
      byType[node.type].power += node.specs.powerWatts;
      byType[node.type].cost += calculateNodeCost(node);
    }

    return Object.values(byType).filter(s => s.count > 0);
  }, [config.nodes]);

  const totalPower = stats.reduce((sum, s) => sum + s.power, 0);
  const totalCost = stats.reduce((sum, s) => sum + s.cost, 0);

  if (stats.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      <div className="grid gap-6 sm:grid-cols-2">
        {/* Power Breakdown */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Zap className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-medium text-gray-700">Power Distribution</span>
            <span className="ml-auto text-sm text-gray-500">{totalPower}W total</span>
          </div>
          <div className="space-y-2">
            {stats.map(stat => (
              <BarRow
                key={stat.type}
                label={NODE_LABELS[stat.type]}
                value={stat.power}
                total={totalPower}
                unit="W"
                color={NODE_COLORS[stat.type].bar}
                count={stat.count}
              />
            ))}
          </div>
        </div>

        {/* Cost Breakdown */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="h-4 w-4 text-green-500" />
            <span className="text-sm font-medium text-gray-700">Cost Distribution</span>
            <span className="ml-auto text-sm text-gray-500">${totalCost.toFixed(0)} total</span>
          </div>
          <div className="space-y-2">
            {stats.map(stat => (
              <BarRow
                key={stat.type}
                label={NODE_LABELS[stat.type]}
                value={stat.cost}
                total={totalCost}
                unit="$"
                prefix
                color={NODE_COLORS[stat.type].bar}
                count={stat.count}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Stacked Bar Visualization */}
      <div className="mt-6">
        <div className="text-xs text-gray-500 mb-2">Node Composition</div>
        <div className="flex h-4 rounded-full overflow-hidden bg-gray-100">
          {stats.map(stat => {
            const percentage = (stat.count / config.nodes.length) * 100;
            return (
              <div
                key={stat.type}
                className={`${NODE_COLORS[stat.type].bar} transition-all duration-300`}
                style={{ width: `${percentage}%` }}
                title={`${NODE_LABELS[stat.type]}: ${stat.count} node${stat.count > 1 ? 's' : ''}`}
              />
            );
          })}
        </div>
        <div className="flex justify-between mt-2">
          {stats.map(stat => (
            <div key={stat.type} className="flex items-center gap-1.5 text-xs">
              <div className={`w-2.5 h-2.5 rounded-full ${NODE_COLORS[stat.type].bar}`} />
              <span className="text-gray-600">
                {NODE_LABELS[stat.type]} ({stat.count})
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface BarRowProps {
  label: string;
  value: number;
  total: number;
  unit: string;
  color: string;
  count: number;
  prefix?: boolean;
}

function BarRow({ label, value, total, unit, color, count, prefix }: BarRowProps) {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  const displayValue = prefix ? `${unit}${value.toFixed(0)}` : `${value}${unit}`;

  return (
    <div className="group">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-gray-600">
          {label}
          <span className="text-gray-400 ml-1">×{count}</span>
        </span>
        <span className="font-medium text-gray-700">{displayValue}</span>
      </div>
      <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
        <div
          className={`h-full rounded-full ${color} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Compact inline power indicator
 */
export function PowerIndicator({ power, maxPower = 200 }: { power: number; maxPower?: number }) {
  const percentage = Math.min((power / maxPower) * 100, 100);
  const status = percentage > 80 ? 'text-red-500' : percentage > 60 ? 'text-amber-500' : 'text-green-500';

  return (
    <div className="flex items-center gap-2">
      <Zap className={`h-4 w-4 ${status}`} />
      <div className="flex-1 h-1.5 rounded-full bg-gray-200">
        <div
          className={`h-full rounded-full transition-all ${
            percentage > 80 ? 'bg-red-500' : percentage > 60 ? 'bg-amber-500' : 'bg-green-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-xs font-medium text-gray-600">{power}W</span>
    </div>
  );
}
